/**
 * demoWeatherModel.ts
 *
 * `demo:weather` (plan §3.4, card D6's broadcast; consumed by card D8b) as the
 * sky and the weather widget read it — one total reader, pure, testable.
 *
 * The hub sends the scripted day's six numbers plus the sim instant they belong
 * to (`simMs`/`iso`). The sky needs exactly two of them (`condition`, `at`) and
 * the widget a small badge; everything else is kept so a later surface (the
 * weather modal's demo chip, a diagnostics panel) needs no second reader.
 *
 * Total on purpose: a partial payload, a numeric string, a missing `simMs`
 * answers `null` for the field and never throws. This runs inside a computed on
 * every socket frame, and a throw there blanks the page that draws the weather.
 */

/** The six numbers plus the instant, as the reader answers them. */
export interface DemoWeatherReading {
  /** An OpenWeatherMap code (`04d`) — the same codes `utils/weatherLook.ts` keys on. */
  condition: string;
  /** Cloud cover, 0–100 %. */
  cloudPct: number;
  /** Rain rate, mm/h. */
  rainMmH: number;
  /** Wind, km/h. */
  windKmh: number;
  /** Air temperature, °C. */
  tempC: number;
  /** Relative humidity, %. */
  humidity: number;
  /** The SIM instant the reading belongs to (epoch ms) — the sky's own `at`. */
  simMs: number;
  /** The same instant as the hub spelled it ('' when it sent none). */
  iso: string;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

/** A finite number from a number or a numeric string, else `null`. */
function number(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** Clamp a percentage-ish number into a range. */
function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * One `demo:weather` frame as the reader sees it, or `null` when the payload is
 * not a weather reading at all (no condition AND no instant — a socket event
 * from another build, a `demo:state` mis-delivered).
 */
export function readDemoWeather(payload: unknown): DemoWeatherReading | null {
  const root = asRecord(payload);
  const condition = typeof root.condition === 'string' ? root.condition.trim() : '';
  const isoRaw = typeof root.iso === 'string' ? root.iso.trim() : '';
  const simMsFromIso = isoRaw !== '' && Number.isFinite(Date.parse(isoRaw)) ? Date.parse(isoRaw) : null;
  const simMs = number(root.simMs) ?? simMsFromIso;
  if (condition === '' && simMs === null) return null;

  return {
    condition,
    cloudPct: clamp(number(root.cloudPct) ?? 0, 0, 100),
    rainMmH: Math.max(0, number(root.rainMmH) ?? 0),
    windKmh: Math.max(0, number(root.windKmh) ?? 0),
    tempC: number(root.tempC) ?? 0,
    humidity: clamp(number(root.humidity) ?? 0, 0, 100),
    simMs: simMs ?? Date.now(),
    iso: isoRaw,
  };
}

/**
 * Does this hub's demo weather own the sky? Only when a reading has arrived AND
 * the demo clock is the app's clock — a stale reading from a demo that has since
 * been turned off must never keep painting a scripted sky (the card's rule: fall
 * back to the real weather when the mode is off).
 */
export function demoWeatherOwnsSky(reading: DemoWeatherReading | null, demoModeEnabled: boolean): boolean {
  return reading !== null && demoModeEnabled && reading.condition !== '';
}
